import React from 'react';
import { motion } from 'framer-motion';
import { ResearchLibraryItem } from '@/hooks/research-generator';
import { ResearchCardHeader, ResearchCardContent, ResearchCardFooter } from './index';
import { useResearchCardData } from './useResearchCardData';

interface ResearchCardItemProps {
  research: ResearchLibraryItem;
  index: number;
  onResearchClick: (researchId: string) => void;
  onToggleFavorite: (researchId: string) => Promise<void>;
  showFavoriteButton?: boolean; // Optional prop to hide favorite button
  contentType?: 'research' | 'private_research'; // NEW: Optional prop for content type
  hideStorageButton?: boolean; // NEW: Optional prop to hide storage button
}

export const ResearchCardItem = React.forwardRef<HTMLDivElement, ResearchCardItemProps>(({
  research,
  index,
  onResearchClick,
  onToggleFavorite,
  showFavoriteButton = true, // Default to true for backward compatibility
  contentType = 'research', // Default to 'research' for backward compatibility
  hideStorageButton = false, // Default to false for backward compatibility
}, ref) => {
  const {
    formattedDate,
    isFavorited,
    favoriteCount,
    researchTypeConfig,
    contentSnippet,
    filterPills,
    locationDisplay,
    sourceTypeDisplay,
    timeframeDisplay,
    typeSpecificTags,
  } = useResearchCardData(research);

  return (
    <motion.div 
      ref={ref}
      key={research.id}
      layoutId={`research-${research.id}`}
      className="group relative cursor-pointer"
      initial={{ 
        opacity: 0, 
        scale: 0.9,
        y: 10
      }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        y: 0
      }}
      exit={{ 
        opacity: 0, 
        scale: 0.9,
        y: -10
      }}
      whileHover={{ 
        scale: 1.02,
        y: -2
      }}
      transition={{
        duration: 0.2,
        ease: [0.25, 0.1, 0.25, 1.0],
        layout: {
          duration: 0.3,
          ease: [0.25, 0.1, 0.25, 1.0]
        }
      }}
      onClick={() => onResearchClick(research.id)}
    >
      {/* Modern card with glass morphism effect */}
      <motion.div 
        className="relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 rounded-2xl p-3 md:p-6 h-full shadow-lg flex flex-col"
        whileHover={{
          borderColor: 'rgba(147, 51, 234, 0.3)', // purple-200/50 equivalent
          boxShadow: '0 25px 50px -12px rgba(147, 51, 234, 0.15)'
        }}
        transition={{
          duration: 0.2,
          ease: [0.25, 0.1, 0.25, 1.0]
        }}
      >
        <ResearchCardHeader
          researchId={research.id}
          researchType={research.research_type}
          researchTypeConfig={researchTypeConfig}
          locationDisplay={locationDisplay}
        />
        
        <div className="flex-1">
          <ResearchCardContent
            title={research.title}
            contentSnippet={contentSnippet}
          />
        </div>
        
        <ResearchCardFooter
          researchId={research.id}
          locationDisplay={locationDisplay}
          sourceTypeDisplay={sourceTypeDisplay}
          timeframeDisplay={timeframeDisplay}
          formattedDate={formattedDate}
          isFavorited={isFavorited}
          favoriteCount={favoriteCount}
          onToggleFavorite={onToggleFavorite}
          showFavoriteButton={showFavoriteButton}
          contentType={contentType}
          hideStorageButton={hideStorageButton}
        />
      </motion.div>
    </motion.div>
  );
});

// Add display name for better debugging
ResearchCardItem.displayName = 'ResearchCardItem';

export default ResearchCardItem;
